import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { api } from "../api/client";
import { hasSessionHint, useAuth } from "../auth/AuthProvider";
import { EmployeeInvitationPage } from "../auth/EmployeeInvitationPage";
import { LoginPage } from "../auth/LoginPage";
import { ForgotPasswordPage, ResetPasswordPage } from "../auth/PasswordRecoveryPages";
import { TenantSelectPage } from "../auth/TenantSelectPage";
import { AnalyticsPage } from "../features/analytics/AnalyticsPage";
import { BookingCreatePage } from "../features/bookings/BookingCreateModal";
import { BookingDetailPage } from "../features/bookings/BookingDetailModal";
import { BookingsPage } from "../features/bookings/BookingsPage";
import { CalendarPage } from "../features/bookings/CalendarPage";
import { ReschedulePage } from "../features/bookings/ReschedulePage";
import { ContactsRoutes } from "../features/contacts/ContactsRoutes";
import { BookingsHomePage } from "../features/dashboard/BookingsHomePage";
import { EventsHomePage } from "../features/dashboard/EventsHomePage";
import { EmailsRoutes } from "../features/emails/EmailsRoutes";
import { EventCreatePage } from "../features/events/EventCreatePage";
import { EventsPage } from "../features/events/EventsPage";
import { EventWorkspacePage } from "../features/events/EventWorkspacePage";
import { FinancePage } from "../features/finance/FinancePage";
import { IntegrationsPage } from "../features/integrations/IntegrationsPage";
import { InventoryPage } from "../features/inventory/InventoryPage";
import { LegalPage } from "../features/legal/LegalPage";
import { MarketingPage } from "../features/marketing/MarketingPage";
import { OnboardingWizard } from "../features/onboarding/OnboardingWizard";
import { OperatorPage } from "../features/operator/OperatorPage";
import { CustomerPortalPage } from "../features/portal/CustomerPortalPage";
import { PortalActionsPage } from "../features/portal/PortalActionsPage";
import { PortalInvitationPage } from "../features/portal/PortalInvitationPage";
import { ProfilePage } from "../features/profile/ProfilePage";
import { PublicBookingPage } from "../features/public/PublicBookingPage";
import { SecuritySettings } from "../features/settings/SecuritySettings";
import { SettingsPage } from "../features/settings/SettingsPage";
import { SponsorsPage } from "../features/sponsors/SponsorsPage";
import { VendorsPage } from "../features/vendors/VendorsPage";
import { VenuesRoutes } from "../features/venues/VenuesRoutes";
import { ErrorPanel } from "../ui/components";
import { RouteErrorBoundary } from "../ui/ErrorBoundary";
import { AppShellSkeleton, BoundarySkeleton } from "../ui/skeletons";
import { AppShell } from "./AppShell";
import { WorkspaceGate } from "./WorkspaceGate";
import { useWorkspace } from "./workspace";

interface PublicSurface {
  customDomain: boolean;
  tenantSlug?: string;
  purpose?: "public_booking" | "customer_portal";
}

// The routes that never sit inside the signed-in frame: sign-in and account recovery,
// the marketing and legal pages, invitations, tenant selection, and the customer-facing
// booking and portal surfaces. Loading states on these paths keep the quiet boundary card even
// when a session hint is present, because the frame would promise navigation that is
// never coming.
const bareSurfacePaths = [
  "/login",
  "/pricing",
  "/legal",
  "/forgot-password",
  "/reset-password",
  "/invite",
  "/booking",
  "/portal",
  "/select-tenant",
];

function expectsAppShell(pathname: string): boolean {
  return !bareSurfacePaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

function SurfaceBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const surface = useQuery({
    queryKey: ["public-surface"],
    queryFn: () => api<PublicSurface>("/api/v1/surface"),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  if (surface.isLoading) {
    return expectsAppShell(location.pathname) && hasSessionHint() ? (
      <AppShellSkeleton />
    ) : (
      <BoundarySkeleton />
    );
  }
  if (surface.error) return <ErrorPanel error={surface.error} />;
  if (surface.data?.customDomain && surface.data.tenantSlug) {
    const destination =
      surface.data.purpose === "customer_portal"
        ? `/portal/${surface.data.tenantSlug}`
        : `/booking/${surface.data.tenantSlug}`;
    const portalSignIn =
      surface.data.purpose === "customer_portal" && location.pathname === "/login";
    const onConfiguredSurface =
      location.pathname === destination || location.pathname.startsWith(`${destination}/`);
    if (!portalSignIn && !onConfiguredSurface) return <Navigate to={destination} replace />;
  }
  return children;
}

function ApplicationBoundary({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  if (auth.loading) return hasSessionHint() ? <AppShellSkeleton /> : <BoundarySkeleton />;
  if (!auth.session) {
    if (location.pathname === "/") return <MarketingPage />;
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

function TenantOnly({ children }: { children: ReactNode }) {
  const auth = useAuth();
  if (!auth.session?.selectedMembership) return <Navigate to="/select-tenant" replace />;
  return children;
}

function LandingPage() {
  const auth = useAuth();
  // Which half of the building this person is looking at decides which home they land on. The two
  // are separate trades over separate money, so one page answering for both told neither story.
  //
  // This used to check the subscription itself and send an owner without one to /onboarding. That
  // check has moved to WorkspaceGate, which applies it to every route rather than only to the one
  // somebody happened to land on first, and to every member rather than only to the owner.
  const { workspace } = useWorkspace();
  const selectedMembership = auth.session?.selectedMembership;
  if (selectedMembership) {
    return workspace === "events" ? <EventsHomePage /> : <BookingsHomePage />;
  }
  if (auth.session?.user.platformRole !== "none")
    return <Navigate to="/operator/overview" replace />;
  return <Navigate to="/select-tenant" replace />;
}

function NotFound() {
  const auth = useAuth();
  return (
    <Navigate
      to={auth.session ? (auth.session.selectedMembership ? "/" : "/select-tenant") : "/login"}
      replace
    />
  );
}

export function App() {
  // The last-resort boundary. The one that matters day to day lives inside AppShell around the
  // outlet, so an ordinary page crash keeps the navigation; this one catches only what renders
  // outside the shell entirely, such as sign-in, the customer portal and public booking, where the
  // alternative really is a blank document.
  return (
    <RouteErrorBoundary>
      <AppRoutes />
    </RouteErrorBoundary>
  );
}

function AppRoutes() {
  return (
    <SurfaceBoundary>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/pricing" element={<MarketingPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite" element={<EmployeeInvitationPage />} />
        <Route path="/booking/:tenantSlug" element={<PublicBookingPage />} />
        <Route path="/portal/:tenantSlug/accept" element={<PortalInvitationPage />} />
        <Route path="/portal/:tenantSlug" element={<CustomerPortalPage />} />
        <Route path="/select-tenant" element={<TenantSelectPage />} />
        <Route
          path="/"
          element={
            <ApplicationBoundary>
              <WorkspaceGate>
                <AppShell />
              </WorkspaceGate>
            </ApplicationBoundary>
          }
        >
          <Route index element={<LandingPage />} />
          <Route
            path="onboarding"
            element={
              <TenantOnly>
                <OnboardingWizard />
              </TenantOnly>
            }
          />
          <Route
            path="bookings"
            element={
              <TenantOnly>
                <BookingsPage />
              </TenantOnly>
            }
          />
          <Route
            path="events"
            element={
              <TenantOnly>
                <EventsPage />
              </TenantOnly>
            }
          />
          <Route
            path="events/new"
            element={
              <TenantOnly>
                <EventCreatePage />
              </TenantOnly>
            }
          />
          <Route
            path="calendar"
            element={
              <TenantOnly>
                <CalendarPage />
              </TenantOnly>
            }
          />
          <Route
            path="calendar/new"
            element={
              <TenantOnly>
                <BookingCreatePage />
              </TenantOnly>
            }
          />
          {/* An event and a booking are different records now. /events/:id used to open a
              booking, so the event workspace sends an identifier it cannot find on to
              /bookings/:id, and links written in the old spelling still reach what they meant. */}
          <Route
            path="events/:eventId"
            element={
              <TenantOnly>
                <EventWorkspacePage />
              </TenantOnly>
            }
          />
          <Route
            path="bookings/:bookingId"
            element={
              <TenantOnly>
                <BookingDetailPage />
              </TenantOnly>
            }
          />
          <Route
            path="calendar/bookings/:bookingId"
            element={
              <TenantOnly>
                <BookingDetailPage />
              </TenantOnly>
            }
          />
          <Route
            path="calendar/bookings/:bookingId/reschedule"
            element={
              <TenantOnly>
                <ReschedulePage />
              </TenantOnly>
            }
          />
          <Route
            path="venues/*"
            element={
              <TenantOnly>
                <VenuesRoutes />
              </TenantOnly>
            }
          />
          <Route
            path="contacts/*"
            element={
              <TenantOnly>
                <ContactsRoutes />
              </TenantOnly>
            }
          />
          <Route
            path="emails/*"
            element={
              <TenantOnly>
                <EmailsRoutes />
              </TenantOnly>
            }
          />
          {/* Accounts and contacts were two records for one customer, and they are one record
              now. Links and bookmarks written in either spelling still land on it. */}
          <Route path="accounts" element={<Navigate to="/contacts" replace />} />
          <Route path="accounts/*" element={<Navigate to="/contacts" replace />} />
          <Route path="customers" element={<Navigate to="/contacts" replace />} />
          <Route path="customers/*" element={<Navigate to="/contacts" replace />} />
          <Route
            path="finance"
            element={
              <TenantOnly>
                <FinancePage />
              </TenantOnly>
            }
          />
          <Route
            path="sponsors"
            element={
              <TenantOnly>
                <SponsorsPage />
              </TenantOnly>
            }
          />
          {/* Suppliers the client hires in. Reading the directory is open to anybody who can see
              the diary, because the panel showing what an event has arranged is part of reading
              that event; committing the client to a supplier is the narrower capability. */}
          <Route
            path="vendors"
            element={
              <TenantOnly>
                <VendorsPage />
              </TenantOnly>
            }
          />
          <Route
            path="inventory"
            element={
              <TenantOnly>
                <InventoryPage />
              </TenantOnly>
            }
          />
          <Route
            path="analytics"
            element={
              <TenantOnly>
                <AnalyticsPage />
              </TenantOnly>
            }
          />
          <Route
            path="portal-inbox"
            element={
              <TenantOnly>
                <PortalActionsPage />
              </TenantOnly>
            }
          />
          <Route
            path="integrations/*"
            element={
              <TenantOnly>
                <IntegrationsPage />
              </TenantOnly>
            }
          />
          <Route
            path="settings/*"
            element={
              <TenantOnly>
                <SettingsPage />
              </TenantOnly>
            }
          />
          {/* Single sign-on and the mail driver are connections to other systems, so they moved
              to Integrations. Links and bookmarks that predate the move still land on them. */}
          <Route path="settings/sso" element={<Navigate to="/integrations/sso" replace />} />
          <Route
            path="settings/messaging"
            element={<Navigate to="/integrations/email" replace />}
          />
          {/* Neither of these is inside TenantOnly, and neither carries a permission. A person's own
              name, colors, password and sessions belong to them rather than to a workspace, so they
              stay reachable for somebody who holds no role on either half of the building and for
              somebody who has not chosen which organisation they are looking at yet. */}
          <Route path="profile" element={<ProfilePage />} />
          <Route path="security" element={<SecuritySettings />} />
          <Route path="operator/*" element={<OperatorPage />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </SurfaceBoundary>
  );
}
